import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserCheck, Loader2, XCircle } from 'lucide-react'
import { delegationsApi } from '@/lib/api'
import type { SchoolMember } from '@/types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'

// The DPO is not a valid delegate (segregation of duties: the step-1
// validator cannot hold step-2 authority); DIRIGENTE/ISPETTORE neither.
const DELEGABLE_ROLES = ['DOCENTE', 'SEGRETERIA']

const SCOPE_LABELS: Array<{ key: 'scope_verdetti' | 'scope_incidenti' | 'scope_notifiche'; label: string; hint: string }> = [
  { key: 'scope_verdetti', label: 'Catena dei verdetti', hint: 'autorizza i verdetti validati dal DPO (passo 2, Art. 14)' },
  { key: 'scope_incidenti', label: 'Incidenti', hint: 'sospende gli strumenti e decide ripristino/revoca' },
  { key: 'scope_notifiche', label: 'Notifiche', hint: 'riceve le notifiche destinate al Dirigente' },
]

export default function DelegationCard({ members }: { members: SchoolMember[] }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [delegatoId, setDelegatoId] = useState('')
  const [scopes, setScopes] = useState({
    scope_verdetti: true, scope_incidenti: false, scope_notifiche: true,
  })
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [validUntil, setValidUntil] = useState('')

  const { data: delegations, isLoading } = useQuery({
    queryKey: ['delegations'],
    queryFn: () => delegationsApi.list(),
  })
  const active = delegations?.[0]

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['delegations'] })

  const createMutation = useMutation({
    mutationFn: () =>
      delegationsApi.create({
        delegato_id: delegatoId,
        ...scopes,
        // from start of day to end of day, local dates
        valid_from: new Date(`${validFrom}T00:00:00`).toISOString(),
        valid_until: new Date(`${validUntil}T23:59:59`).toISOString(),
      }),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Delega conferita' })
      setDelegatoId('')
      setValidUntil('')
    },
    onError: (err: any) =>
      toast({
        title: 'Delega non creata',
        description: err?.response?.data?.detail ?? 'Errore imprevisto',
        variant: 'destructive',
      }),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => delegationsApi.revoke(id),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Delega revocata' })
    },
  })

  const candidates = members.filter((m) => DELEGABLE_ROLES.includes(m.role) && m.is_active)
  const anyScope = scopes.scope_verdetti || scopes.scope_incidenti || scopes.scope_notifiche

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-mim-blue" />
          Delega del Dirigente (vicario)
        </CardTitle>
        <CardDescription className="text-xs">
          Delega temporale dei poteri di governance a un docente o alla segreteria, con
          perimetro a tua scelta. Ogni atto in delega è registrato nell'audit con doppia
          attribuzione (delegato e delegante). Il DPO non è delegabile.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? null : active ? (
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium">{active.delegato_name}</p>
              {active.attiva ? (
                <Badge className="bg-verde text-white text-xs">Attiva</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Programmata</Badge>
              )}
              <span className="text-xs text-muted-foreground">
                dal {new Date(active.valid_from).toLocaleDateString('it-IT')} al{' '}
                {new Date(active.valid_until).toLocaleDateString('it-IT')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {active.scope_verdetti && <Badge variant="outline" className="text-xs">Catena dei verdetti</Badge>}
              {active.scope_incidenti && <Badge variant="outline" className="text-xs">Incidenti</Badge>}
              {active.scope_notifiche && <Badge variant="outline" className="text-xs">Notifiche</Badge>}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-rosso border-rosso/40 hover:bg-rosso-light mt-1"
              onClick={() => {
                if (window.confirm(`Revocare la delega a ${active.delegato_name}? La revoca è registrata nell'audit.`)) {
                  revokeMutation.mutate(active.id)
                }
              }}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <XCircle className="w-3.5 h-3.5 mr-1.5" />}
              Revoca delega
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Delegato</Label>
                <Select value={delegatoId || undefined} onValueChange={setDelegatoId}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Scegli un membro…" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.length === 0 ? (
                      <SelectItem value="none" disabled>Nessun membro delegabile</SelectItem>
                    ) : candidates.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Dal</Label>
                <Input type="date" className="h-9 text-sm" value={validFrom}
                       onChange={(e) => setValidFrom(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Al (incluso)</Label>
                <Input type="date" className="h-9 text-sm" value={validUntil} min={validFrom}
                       onChange={(e) => setValidUntil(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Perimetro della delega</Label>
              {SCOPE_LABELS.map(({ key, label, hint }) => (
                <label key={key} className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={scopes[key]}
                    onCheckedChange={(v) => setScopes((s) => ({ ...s, [key]: v === true }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground block">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!delegatoId || !validUntil || !anyScope || createMutation.isPending}
              className="bg-mim-blue hover:bg-blue-800 text-white"
              size="sm"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Conferisci delega
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
